// CMP-Button et CMP-IconButton (Lot 1b). Maquette : hauteur 40 px, rayon 6, marge 0 14 px, 14 px semi-gras.
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, type LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-md font-semibold whitespace-nowrap transition-colors duration-[var(--wg-dur-fast)] disabled:cursor-not-allowed disabled:bg-border disabled:text-text-disabled disabled:border-border',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-on-primary hover:bg-primary-hover',
        secondary: 'bg-secondary text-on-secondary hover:bg-secondary-hover',
        outline: 'border border-border-strong bg-surface text-text hover:bg-surface-2',
        ghost: 'bg-transparent text-text hover:bg-surface-2',
        danger: 'bg-error text-surface hover:opacity-90',
      },
      size: {
        sm: 'h-8 px-3 text-small',
        md: 'h-10 px-3.5 text-small',
        lg: 'h-12 px-4 text-h4',
      },
    },
    defaultVariants: { variant: 'outline', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  icon?: LucideIcon;
  iconEnd?: LucideIcon;
  /** Chargement : spinner à la place de l'icône, libellé conservé (Lot 1b). */
  loading?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, icon: Icon, iconEnd: IconEnd, loading = false, disabled, children, type = 'button', ...props },
  ref,
) {
  const iconClass = size === 'lg' ? 'size-5 shrink-0' : 'size-[18px] shrink-0';
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {loading ? <Loader2 aria-hidden="true" className={cn(iconClass, 'animate-spin')} /> : Icon && <Icon aria-hidden="true" className={iconClass} />}
      {children}
      {IconEnd && <IconEnd aria-hidden="true" className={iconClass} />}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: LucideIcon;
  /** aria-label obligatoire (Lot 1b). */
  label: string;
  variant?: 'ghost' | 'outline';
  size?: 'md' | 'lg';
  /** Compteur (non lus) affiché en pastille. */
  badge?: number;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, variant = 'ghost', size = 'md', badge, className, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={badge ? `${label} (${badge})` : label}
      title={label}
      className={cn(
        'relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md text-text-2 transition-colors hover:bg-surface-2 hover:text-text disabled:cursor-not-allowed disabled:opacity-50',
        size === 'md' ? 'size-10' : 'size-12',
        variant === 'outline' && 'border border-border-strong bg-surface',
        className,
      )}
      {...props}
    >
      <Icon aria-hidden="true" className="size-5" />
      {badge ? (
        <span aria-hidden="true" className="absolute top-1 right-1 min-w-4 rounded-full bg-error px-1 text-center text-[11px] leading-4 font-bold text-surface">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </button>
  );
});
